// Monta o backend do Supabase a partir do .env (URL + chave anon). Sem ele, null: o app avisa
// "Servidor não configurado" — não existe mais modo demonstração (AC-234).
import { AUTH_CALLBACK_URL, createSupabaseAuth, type OAuthProvider } from './auth';
import type { Backend } from './repository';
import { createSupabase, readSupabaseEnv, type SupabaseEnv } from './supabaseClient';
import { createSupabaseBackend } from './supabaseRepository';
import { desktopOAuthSignIn, handleAuthCallback } from '../platform/oauth';
import {
  completeDesktopIdTokenLogin,
  createIdTokenLogin,
  finishWebIdTokenLogin,
  memoryPending,
  readOAuthClientIds,
  sessionPending,
  SITE_AUTH_CALLBACK_URL,
  visibleOAuthProviders,
} from '../platform/idTokenOAuth';
import { createSecureStorage } from '../platform/secureStorage';
import type { DesktopBridge } from '../platform/desktop';
import { getUpdater, type UpdaterBridge } from '../platform/updater';
import { getAutostart, type AutostartBridge } from '../platform/autostart';

export interface AppEnvironment {
  backend: Backend;
  desktop: DesktopBridge | null;
  storage: Storage | null;
  /** atualizador do app desktop (null na web — AC-206) */
  updater?: UpdaterBridge | null;
  /** iniciar com o computador (null na web — AC-129) */
  autostart?: AutostartBridge | null;
  /** botões de login social; sem a lista, Google e Microsoft (AC-240) */
  oauthProviders?: readonly OAuthProvider[];
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export async function createEnvironment(
  desktop: DesktopBridge | null,
  env: SupabaseEnv | null = readSupabaseEnv()
): Promise<AppEnvironment | null> {
  const storage = safeStorage();
  const updater = desktop ? await getUpdater() : null;
  const autostart = desktop ? await getAutostart() : null;

  if (env) {
    const secure = desktop ? createSecureStorage(desktop.invoke) : undefined;
    const client = createSupabase(env, secure);
    // login próprio com Google e Microsoft (US-048): no desktop volta pela página do site, na web pelo próprio app
    const pending = desktop ? memoryPending() : sessionPending(window.sessionStorage);
    const auth = createSupabaseAuth(client, {
      siteUrl: desktop ? AUTH_CALLBACK_URL : window.location.origin,
      oauthIdToken: createIdTokenLogin({
        clientIds: readOAuthClientIds(),
        redirectUri: desktop ? SITE_AUTH_CALLBACK_URL : `${window.location.origin}/`,
        pending,
        navigate: desktop ? desktop.openExternal : (url) => window.location.assign(url),
      }),
      oauthDesktop: desktop ? (provider) => desktopOAuthSignIn({ auth: client.auth, openExternal: desktop.openExternal }, provider) : undefined,
    });
    if (desktop) {
      await desktop.onDeepLink((url) => {
        void handleAuthCallback(url, client.auth);
        void completeDesktopIdTokenLogin(url, pending, client.auth);
      });
    } else {
      await finishWebIdTokenLogin(window, pending, client.auth);
    }
    return {
      backend: createSupabaseBackend(client, auth, desktop ? 'desktop' : 'web'),
      desktop,
      storage,
      updater,
      autostart,
      oauthProviders: visibleOAuthProviders(readOAuthClientIds()),
    };
  }

  return null;
}
